import { FC, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Paper, Skeleton, Typography } from "@mui/material";
import {
  DataGridPro as DataGrid,
  GridColDef,
  GridLoadingOverlay,
} from "@mui/x-data-grid-pro";
import ManageSearch from "@mui/icons-material/ManageSearch";
import { isNumber } from "lodash";
import __ from "src/utils/Translations";
import useBasePath from "src/utils/hooks/useBasePath";
import { LogLevel, useLogStatus } from "src/utils/newRelic/useLogStatus";
import formatTimeAmount from "src/utils/Company/Absences/formatTimeAmount";
import translateOperationCode from "src/utils/Company/Absences/translateOperationCode";
import formatEmptyValue from "src/utils/Company/Absences/formatEmptyValue";
import { useFetchEmployee } from "src/Pages/Company/Absences/api/employee.queries";
import {
  useGetAbsenceType,
  useFetchAbsenceTypeBalanceHistory,
} from "src/Pages/Company/Absences/api/absenceTypes.queries";
import GridLinkAction from "src/Pages/Company/Absences/Tables/GridLinkAction";
import BreadcrumbsMui from "src/Components/BreadcrumbsMui/BreadcrumbsMui";
import Title from "src/Pages/Company/Absences/Title/Title";
import EmployeeCard from "src/Pages/Company/Absences/EmployeeCard";
import ErrorDataFetching from "src/Components/Error/ErrorDataFetching";
import { IAbsenceTypeBalanceHistoryItem } from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";

const AbsenceRecordsAbsenceTypeHistory: FC = () => {
  const { employeeId, absenceTypeId } = useParams<{
    absenceTypeId: string;
    employeeId: string;
  }>();
  const basePath = useBasePath();
  const logStatus = useLogStatus();

  if (!employeeId) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No employee ID provided.",
      file: "AbsenceRecordsAbsenceTypeHistory.tsx",
    });
  }

  if (!absenceTypeId) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No Absence Type ID provided.",
      file: "AbsenceRecordsAbsenceTypeHistory.tsx",
    });
  }

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const requestParams = useMemo(
    () => ({
      employeeId,
      absenceTypeId: absenceTypeId || "",
      page: paginationModel.page + 1,
      size: paginationModel.pageSize,
    }),
    [absenceTypeId, employeeId, paginationModel.page, paginationModel.pageSize]
  );

  const { data: employee, isError: isErrorEmployee } =
    useFetchEmployee(employeeId);

  const {
    data: dataAbsenceType,
    isFetching: isFetchingAbsenceType,
    isError: isErrorAbsenceType,
  } = useGetAbsenceType(absenceTypeId);

  const { data, isFetching } = useFetchAbsenceTypeBalanceHistory(requestParams);

  const columns = useMemo(
    () =>
      [
        {
          headerName: __("Data"),
          field: "operationTime",
          type: "dateTime",
          width: 175,
          valueGetter: (value) => (value ? new Date(value) : "-"),
        },
        {
          headerName: __("Okres"),
          field: "billingCycle",
          type: "string",
          valueGetter: (_value, row) => {
            if (!row.billingCycle) return "-";

            const {
              billingCycle: { year, yearMonth },
            } = row;

            if (yearMonth) {
              const [yearPart, monthPart] = yearMonth.split("-");
              return `${monthPart}.${yearPart}`;
            }

            return year;
          },
          width: 120,
        },
        {
          headerName: __("Liczba dni"),
          field: "balanceChangeDays",
          type: "number",
          valueGetter: (_value, row) =>
            formatTimeAmount(row.balanceChange, "days"),
          width: 120,
        },
        {
          headerName: __("Liczba godzin"),
          field: "balanceChangeHours",
          type: "number",
          valueGetter: (_value, row) =>
            formatTimeAmount(row.balanceChange, "hours"),
          width: 120,
        },
        {
          headerName: __("Bilans po operacji"),
          field: "balanceAfterOperation",
          type: "number",
          valueGetter: (_value, row) => {
            if (!row.balanceAfterOperation) return "-";

            return `${row.balanceAfterOperation.timeAmount.days} / ${row.balanceAfterOperation.timeAmount.hours}`;
          },
          width: 150,
        },
        {
          headerName: __("Rodzaj operacji"),
          field: "operationInfo",
          type: "string",
          valueGetter: (_value, row) =>
            translateOperationCode(row.operationInfo.operation, row.requester),
          width: 200,
        },
        {
          headerName: __("Notatka"),
          field: "note",
          type: "string",
          width: 450,
          valueGetter: (_value, row) => row.operationInfo.note,
          valueFormatter: formatEmptyValue,
        },
        {
          headerName: __("Akcja"),
          field: "actions",
          type: "actions",
          width: 120,
          align: "right",
          getActions: (params) => {
            const {
              row: {
                operationInfo: { absenceRequestId },
              },
            } = params;

            const previewLink = `${basePath}/absences/absence-request/${absenceRequestId}/preview`;

            if (absenceRequestId) {
              return [
                <GridLinkAction
                  key="preview"
                  icon={<ManageSearch />}
                  label={__("Podgląd")}
                  bgColor="info.main"
                  bgColorHover="info.dark"
                  to={previewLink}
                />,
              ];
            }
            return [];
          },
        },
      ] as GridColDef<IAbsenceTypeBalanceHistoryItem>[],
    [basePath]
  );

  const rowCountRef = useRef(data?.totalElements || 0);
  const rowCount = useMemo(() => {
    if (isNumber(data?.totalElements)) {
      rowCountRef.current = data.totalElements;
    }
    return rowCountRef.current;
  }, [data?.totalElements]);

  if (isErrorAbsenceType || isErrorEmployee) {
    return <ErrorDataFetching />;
  }

  return (
    <>
      <BreadcrumbsMui
        items={[
          {
            label: __("Nieobecności"),
            to: `${basePath}/absences`,
          },
          {
            label: __("Kartoteki pracowników"),
            to: `${basePath}/absences/absence-records`,
          },
          {
            label: __("Wymiary nieobecności i obecności"),
            to: `${basePath}/absences/employee/${employeeId}#balances`,
          },
          {
            label: __("Szczegółowa historia"),
          },
        ]}
      />
      <Title
        title={`${__("Szczegółowa historia")} ${dataAbsenceType?.name}`}
        subtitle={__(
          "Szczegółowa historia dla wszystkich akcji wykonanych na pracowniku."
        )}
      />

      <EmployeeCard data={employee} />

      <Paper sx={{ padding: "1rem", mt: "1.5rem" }}>
        <DataGrid
          columns={columns}
          rows={data?.content || []}
          getRowId={(row) => `${row.artificialId}-${row.operationTime}`}
          rowCount={rowCount}
          loading={isFetching}
          initialState={{ pinnedColumns: { left: [], right: ["actions"] } }}
          disableColumnMenu
          disableColumnSelector
          disableDensitySelector
          disableRowSelectionOnClick
          disableColumnSorting
          disableColumnFilter
          disableEval
          showCellVerticalBorder={false}
          showColumnVerticalBorder={false}
          density="comfortable"
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          slots={{
            headerFilterMenu: null,
            loadingOverlay: () => (
              <GridLoadingOverlay
                variant="linear-progress"
                noRowsVariant="skeleton"
              />
            ),
          }}
        />
      </Paper>
    </>
  );
};

export default AbsenceRecordsAbsenceTypeHistory;
